/**
 * @file `EmailsLayout` — the persistent left-rail shell for the `/emails/*`
 *       sub-app. Renders the panel-split rail (Compose / History /
 *       Campaigns / Suppressions, with live counts) beside a routed
 *       `<Outlet/>`, so the rail stays put as the operator moves between
 *       the sub-app's screens.
 * @module @labcraft/admin/ui/emails/emails-layout
 *
 * Replaces the per-panel `<EmailsSubnav/>` tab strip: the rail is a route
 * layout (react-router nested routes) rather than chrome each panel drew
 * itself. Active state tracks the URL — `NavLink` sets `aria-current="page"`
 * on the matched item, which `.panel-rail__item[aria-current='page']`
 * (panels.css) styles — and the Campaigns / Suppressions counts are fetched
 * once here (`useCampaigns` / `useSuppressions`, react-query deduped with the
 * panels' own reads) instead of per screen. Compose is `end`-matched so it
 * stays lit only on `/emails` exactly, not the nested campaign routes.
 */

import type { JSX } from 'react'
import { NavLink, Outlet } from 'react-router'
import { useCampaigns, useSuppressions } from '../hooks/index.ts'

/**
 * One rail destination.
 *
 * @property to - The route path the item links to.
 * @property label - The rail label.
 * @property end - Restrict `NavLink` matching to the exact path (Compose).
 * @property count - Optional live count badge.
 */
type RailItem = {
  to: string
  label: string
  end?: boolean
  count?: number
}

/** Render the emails sub-app rail beside the routed screen. */
export function EmailsLayout(): JSX.Element {
  const campaigns = useCampaigns()
  const suppressions = useSuppressions()

  const items: readonly RailItem[] = [
    { to: '/emails', label: 'Compose', end: true },
    { to: '/emails/history', label: 'History' },
    {
      to: '/emails/campaigns',
      label: 'Campaigns',
      count: campaigns.data?.campaigns.length,
    },
    {
      to: '/emails/suppressions',
      label: 'Suppressions',
      count: suppressions.data?.suppressions.length,
    },
  ]

  return (
    <div className="panel-split">
      <nav className="panel-rail" aria-label="Emails sections">
        <div className="panel-rail__h">Emails</div>
        {items.map(it => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className="panel-rail__item">
            <span>{it.label}</span>
            {it.count !== undefined && (
              <span className="panel-rail__count">{it.count}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </div>
  )
}
